import React, { useCallback, useContext, useMemo, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'

import { FlightDropdownOption } from 'components/Flights/types'
import { fareTypes, fareCabins, FlightsFareTypes } from 'constants/flight'
import { formatOccupantsShort } from 'lib/occupancyUtils'
import { validateFlightOccupants } from 'lib/flights'
import FlightTravellerFloatingPanel from 'components/SearchV2/Components/Mobile/FlightTravellerFloatingPanel/FlightTravellerFloatingPanel'
import DateFloatingPanel from 'components/SearchV2/Components/Mobile/DateSearchFloatingPanel/DateFloatingPanel'
import SearchFormFieldGroup from 'components/SearchV2/Components/SearchFormField/SearchFormFieldGroup'
import FareDropdownMobile from 'components/SearchV2/Components/Mobile/FareDropdown/FareDropdownMobile'
import SearchFormField from 'components/SearchV2/Components/SearchFormField/SearchFormField'
import useStepsController from 'components/SearchV2/Hooks/useStepsController'
import useDateFlightSearchProps from 'components/SearchV2/Hooks/useDateFlightSearchProps'
import { formatDate, getAirportDescription } from 'components/SearchV2/Utils/GlobalSearchV2'
import { SEARCH_VERTICALS } from 'constants/search'
import FlightAirportFloatingPanel from '../FlightAirportField/FlightAirportFloatingPanel'

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  > * + * {
    margin-top: ${rem(8)}
  }
`

const Faredropdowns = styled.div`
  display: flex;

  & > * + * {
    margin-left: ${rem(24)};
  }
`

enum searchSteps {
  DEPARTURE = 'departure-airport',
  ARRIVAL = 'arrival-airport',
  DATE = 'date',
  TRAVELLERS = 'travellers'
}

interface Props {
  onSearch: () => void;
}

function FlightsTabContent(props: Props) {
  const { onSearch } = props

  const state = useContext(FlightSearchWidgetStateContext)
  const dispatch = useContext(FlightSearchWidgetDispatchContext)

  const {
    fareType,
    fareCabin,
    departureAirport,
    arrivalAirport,
    checkinDate,
    checkoutDate,
    occupants,
    showDepartureError,
    showDestinationError,
    showTravelDatesError,
    showSameAirportError,
  } = state

  const invalidTravellers = useMemo(() => validateFlightOccupants(occupants), [occupants])

  const stepsValidation = useMemo(() => ({
    [searchSteps.DEPARTURE]: departureAirport,
    [searchSteps.ARRIVAL]: arrivalAirport,
    [searchSteps.DATE]: checkinDate && checkoutDate,
    [searchSteps.TRAVELLERS]: !invalidTravellers,
  }), [departureAirport, arrivalAirport, checkinDate, checkoutDate, invalidTravellers])

  const [activeStep, setActiveStep] = useState<searchSteps>()
  const {
    goToNextStep,
    goToPreviousStep,
    closeActiveStep,
    isSearchEnabled,
  } = useStepsController({ activeStep, setActiveStep, stepsValidation, onSearch })

  const dateSearchProps = useDateFlightSearchProps()

  const { checkin, checkout } = useMemo(() => ({
    checkin: formatDate(checkinDate),
    checkout: formatDate(checkoutDate),
  }), [checkinDate, checkoutDate])

  const changeFareType = useCallback((fareType: FlightDropdownOption) => {
    dispatch({ type: FlightSearchWidgetActions.SET_FARE_TYPE, fareType })
  }, [dispatch])

  const changeFareCabin = useCallback((fareCabin: FlightDropdownOption) => {
    dispatch({ type: FlightSearchWidgetActions.SET_FARE_CABIN, fareCabin })
  }, [dispatch])

  const openDepartureSearchStep = useCallback(() => setActiveStep(searchSteps.DEPARTURE), [])
  const openArrivalSearchStep = useCallback(() => setActiveStep(searchSteps.ARRIVAL), [])
  const openDateSearchStep = useCallback(() => setActiveStep(searchSteps.DATE), [])
  const openTravellerSearchStep = useCallback(() => setActiveStep(searchSteps.TRAVELLERS), [])

  return (
    <ContentWrapper>
      <Faredropdowns>
        <FareDropdownMobile
          options={fareTypes}
          title="Flight type"
          selectedOption={fareType}
          onOptionChange={changeFareType}
        />
        <FareDropdownMobile
          options={fareCabins}
          title="Class"
          selectedOption={fareCabin}
          onOptionChange={changeFareCabin}
        />
      </Faredropdowns>

      <SearchFormField
        label="From"
        placeholder="Search by country, city or airport"
        value={getAirportDescription(departureAirport)}
        onClick={openDepartureSearchStep}
        hasError={showDepartureError || showSameAirportError}
      />

      <SearchFormField
        label="To"
        placeholder="Search by country, city or airport"
        value={getAirportDescription(arrivalAirport)}
        onClick={openArrivalSearchStep}
        hasError={showDestinationError || showSameAirportError}
      />

      <SearchFormFieldGroup>
        <SearchFormField
          label="Departure"
          placeholder="Select date"
          value={checkin}
          onClick={openDateSearchStep}
          hasError={showTravelDatesError}
        />
        <SearchFormField
          label="Returning"
          placeholder="Select date"
          value={checkout}
          onClick={openDateSearchStep}
          hasError={showTravelDatesError}
          hidden={fareType.value === FlightsFareTypes.ONE_WAY}
        />
      </SearchFormFieldGroup>

      <SearchFormField
        label="Travellers"
        value={formatOccupantsShort(occupants)}
        onClick={openTravellerSearchStep}
        hasError={invalidTravellers}
      />

      <FlightAirportFloatingPanel
        isOpen={activeStep === searchSteps.DEPARTURE}
        onClose={closeActiveStep}
        onGoBack={goToPreviousStep}
        onConfirm={goToNextStep}
        isDeparture
      />
      <FlightAirportFloatingPanel
        isOpen={activeStep === searchSteps.ARRIVAL}
        onClose={closeActiveStep}
        onGoBack={goToPreviousStep}
        onConfirm={goToNextStep}
      />
      <DateFloatingPanel
        {...dateSearchProps}
        isOpen={activeStep === searchSteps.DATE}
        onClose={closeActiveStep}
        onGoBack={goToPreviousStep}
        onConfirm={goToNextStep}
        confirmText={isSearchEnabled ? 'Search' : 'Apply'}
        searchVertical={SEARCH_VERTICALS.FLIGHTS}
      />
      <FlightTravellerFloatingPanel
        isOpen={activeStep === searchSteps.TRAVELLERS}
        onClose={closeActiveStep}
        onGoBack={goToPreviousStep}
        onConfirm={goToNextStep}
        confirmText={isSearchEnabled ? 'Search' : 'Apply'}
      />
    </ContentWrapper>
  )
}

export default FlightsTabContent
